import {normalizeInputRequest, optionIdForLabel} from '@github-ui/ahp-relay/ahp-session-selectors'
import {
  selectedAnswer,
  selectedManyAnswer,
  textAnswer,
  type BuildInputCompletedActionParams,
} from '@github-ui/ahp-relay/ahp-input-actions'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {ChatInputAnswer, ChatInputRequest} from '@microsoft/agent-host-protocol'

import {COMMAND_TYPES, type CommandType} from './command-types'
import type {
  AskUserArguments,
  ElicitationAskUserArguments,
  ExitPlanModeArguments,
  NormalizedPrompt,
  PlanResolution,
  RequestedSchema,
  RequestedSchemaProperty,
  SimpleAskUserArguments,
} from './interactive-prompt-types'

/**
 * Pure conversions between AHP `chat/input` requests and the normalized prompt
 * shape the Copilot composers render. They live here, below agent sessions, so
 * the established session composer can take over a pending prompt without
 * depending on the agent sessions package.
 */

// AHP question kinds are const enums — compare wire literals.
const QUESTION_KIND_SINGLE_SELECT = 'single-select'
const QUESTION_KIND_MULTI_SELECT = 'multi-select'

/** The shape SimpleAskUserPromptContent sends for an ask_user response. */
interface AskUserResponsePayload {
  answer: string
  wasFreeform?: boolean
  dismissed?: boolean
}

/** The shape ElicitationAskUserPromptContent sends for an elicitation response. */
interface ElicitationResponsePayload {
  action: 'accept' | 'decline'
  content?: Record<string, string | string[]>
}

/**
 * Build an elicitation schema from a multi-question AHP request so the shared
 * {@link ElicitationAskUserDisplay} can render it. Each question becomes one
 * schema property; select questions expose their options as `enum` +
 * `enumLabels`, and multi-select questions are typed as arrays.
 */
function toRequestedSchema(request: ChatInputRequest): RequestedSchema {
  const properties: Record<string, RequestedSchemaProperty> = {}
  const required: string[] = []

  for (const question of request.questions ?? []) {
    const isSelect = question.kind === QUESTION_KIND_SINGLE_SELECT || question.kind === QUESTION_KIND_MULTI_SELECT
    const options = isSelect ? ((question as {options?: Array<{id: string; label: string}>}).options ?? []) : []
    const enumLabels: Record<string, string> = {}
    for (const option of options) enumLabels[option.id] = option.label

    properties[question.id] = {
      title: question.title ?? question.message,
      description: question.message,
      ...(question.kind === QUESTION_KIND_MULTI_SELECT ? {type: 'array', multiSelect: true} : {}),
      ...(options.length > 0 ? {enum: options.map(o => o.id), enumLabels} : {}),
    }
    if (question.required) required.push(question.id)
  }

  return {properties, ...(required.length > 0 ? {required} : {})}
}

// Question ids the host uses for an exit-plan-mode request (copilot-host input_bridge.rs).
const PLAN_ACTION_QUESTION_ID = 'selectedAction'
const PLAN_FEEDBACK_QUESTION_ID = 'feedback'

function planActionQuestion(request: ChatInputRequest) {
  const question = request.questions?.find(q => q.id === PLAN_ACTION_QUESTION_ID)
  return question?.kind === QUESTION_KIND_SINGLE_SELECT ? question : undefined
}

/**
 * Map the host's exit-plan-mode approval request onto
 * {@link ExitPlanModeArguments}, or `null` for any other request. The host
 * sends `summary + "\n\n" + plan` as one message, which can't be split reliably
 * when the summary has paragraphs, so the card previews the first paragraph
 * and View plan shows the full message.
 */
export function ahpPlanRequestToArguments(request: ChatInputRequest): ExitPlanModeArguments | null {
  const question = isFeatureEnabled('one_copilot_plan_mode') ? planActionQuestion(request) : undefined
  if (!question) return null

  const message = request.message ?? ''
  const splitAt = message.indexOf('\n\n')
  return {
    summary: splitAt === -1 ? message : message.slice(0, splitAt),
    planContent: splitAt === -1 ? undefined : message,
    actions: question.options.map(option => option.id),
    recommendedAction: question.options.find(option => option.recommended)?.id,
    requestId: request.id,
  }
}

function submittedValue(request: ChatInputRequest, questionId: string): string | undefined {
  const answer = request.answers?.[questionId]
  if (!answer || !('value' in answer) || answer.state !== 'submitted') return undefined
  return typeof answer.value.value === 'string' ? answer.value.value : undefined
}

/** The user's resolution of a completed exit-plan-mode request. */
export function ahpPlanResolution(
  request: ChatInputRequest,
  response: 'accept' | 'decline' | 'cancel',
): PlanResolution {
  const feedback = submittedValue(request, PLAN_FEEDBACK_QUESTION_ID) || undefined
  if (response !== 'accept') return {approved: false, feedback}
  return {approved: true, selectedAction: submittedValue(request, PLAN_ACTION_QUESTION_ID), feedback}
}

/**
 * Map an AHP {@link ChatInputRequest} onto the same {@link NormalizedPrompt}
 * shape the composer already renders for CLI/events prompts, so the existing
 * `PromptInput` -> `UserChoiceCard` components can be reused unchanged. Returns
 * `null` for requests with no questions.
 */
export function ahpInputRequestToPrompt(request: ChatInputRequest): NormalizedPrompt | null {
  const plan = ahpPlanRequestToArguments(request)
  if (plan) return {toolCallId: request.id, toolName: 'exit_plan_mode', arguments: plan}

  const normalized = normalizeInputRequest(request)
  if (!normalized) return null

  const args: AskUserArguments = normalized.isElicitation
    ? ({
        message: normalized.question,
        requestedSchema: toRequestedSchema(request),
      } satisfies ElicitationAskUserArguments)
    : ({
        question: normalized.question,
        choices: normalized.choices,
        allowFreeform: normalized.allowFreeform,
      } satisfies SimpleAskUserArguments)

  return {toolCallId: request.id, toolName: 'ask_user', arguments: args}
}

function firstQuestionKind(request: ChatInputRequest): string | undefined {
  return (request.questions ?? [])[0]?.kind
}

/**
 * Translate a composer `onRespond` payload into `chat/inputCompleted` params for
 * a given AHP request. Pure and exported for unit testing.
 *
 * - ask_user choice -> accept with a `selected` answer (label mapped to option id)
 * - ask_user freeform -> accept with the freeform text (as `text` or `selected` freeform)
 * - ask_user skip/dismiss -> cancel
 * - elicitation accept/decline -> accept (answers built from content) / decline
 */
export function ahpCompletionFromResponse(
  request: ChatInputRequest,
  response: unknown,
  commandType: CommandType,
): BuildInputCompletedActionParams | null {
  const requestId = request.id

  if (commandType === COMMAND_TYPES.ASK_USER_RESPONSE) {
    const payload = response as AskUserResponsePayload
    if (payload.dismissed) return {requestId, response: 'cancel'}

    const questionId = (request.questions ?? [])[0]?.id
    if (!questionId) return null
    const isTextQuestion = firstQuestionKind(request) === 'text'

    if (payload.wasFreeform) {
      const answer: ChatInputAnswer = isTextQuestion ? textAnswer(payload.answer) : selectedAnswer('', [payload.answer])
      return {requestId, response: 'accept', answers: {[questionId]: answer}}
    }

    const answer: ChatInputAnswer = isTextQuestion
      ? textAnswer(payload.answer)
      : selectedAnswer(optionIdForLabel(request, questionId, payload.answer) ?? payload.answer)
    return {requestId, response: 'accept', answers: {[questionId]: answer}}
  }

  if (commandType === COMMAND_TYPES.ELICITATION_RESPONSE) {
    const payload = response as ElicitationResponsePayload
    if (payload.action === 'decline') return {requestId, response: 'decline'}

    const kindById = new Map((request.questions ?? []).map(q => [q.id, q.kind]))
    const answers: Record<string, ChatInputAnswer> = {}
    for (const [questionId, value] of Object.entries(payload.content ?? {})) {
      const kind = kindById.get(questionId)
      if (Array.isArray(value)) {
        // Multi-select: the values are selected option ids.
        answers[questionId] = selectedManyAnswer(value)
      } else if (kind === QUESTION_KIND_SINGLE_SELECT) {
        // Single-select: the value is a selected option id, not free text.
        answers[questionId] = selectedAnswer(value)
      } else {
        answers[questionId] = textAnswer(value)
      }
    }
    return {requestId, response: 'accept', answers}
  }

  if (commandType === COMMAND_TYPES.PLAN_APPROVAL_RESPONSE) {
    const payload = response as PlanResolution
    const feedback: Record<string, ChatInputAnswer> = payload.feedback
      ? {[PLAN_FEEDBACK_QUESTION_ID]: textAnswer(payload.feedback)}
      : {}
    // The host forwards feedback to the agent on decline only; cancel drops it.
    if (!payload.approved) return {requestId, response: 'decline', answers: feedback}
    if (!payload.selectedAction) return null
    return {
      requestId,
      response: 'accept',
      answers: {[PLAN_ACTION_QUESTION_ID]: selectedAnswer(payload.selectedAction), ...feedback},
    }
  }

  return null
}
